import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useUser } from "../../../../../../providers/UserProvider";
import musicIcon from "../../../pics/icons/musicIcon.png"
import playNext from "../../../pics/icons/play.png"
import OptionButton from '../OptionButton';
import CurrentlyPlayingAnimation from "./CurrentlyPlayingAnimation";
import { callBackUrl } from "../../../constants";
import SpotifyScrubber from "./SpotifyScrubber"
import SpotifyVolumeSlider from "./SpotifyVolumeSlider"

const SpotifyWidget = ({ currentEncounter, setCurrentEncounter, socket }) => {

    const { username, spotifyInfo, prefs } = useUser();

    const spotifyWidgetRef = useRef(null);
    const [showSpotifyControls, setShowSpotifyControls] = useState(false);

    const [player, setPlayer] = useState(null);
    const [deviceId, setDeviceId] = useState(null);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [isPaused, setIsPaused] = useState(true);
    const [isShuffling, setIsShuffling] = useState(false);
    const [repeatMode, setRepeatMode] = useState("track");

    const [accessToken, setAccessToken] = useState(null);
    const [playlists, setPlaylists] = useState(null);
    const [selectedPlaylist, setSelectedPlaylist] = useState(null);
    const [playlistTracks, setPlaylistTracks] = useState([]);
    const [autoPlay, setAutoPlay] = useState(prefs?.spotifyAutoPlay);
    const [musicShake, setMusicShake] = useState(false);
    const [showNextSong, setShowNextSong] = useState(currentEncounter?.spotifyEncounterSongInfo?.currentTrackId);
    const [headers, setHeaders] = useState({ Authorization: `Bearer ${accessToken}`});
    
    const playerRef = useRef(null);
    const accessTokenRef = useRef(accessToken);
    const playlistTracksCache = useRef({});

    // Spotify login URL
    const client_id = "d9843102640e4cc6b326ff3f9f017add";
    const state = encodeURIComponent(`${socket.id}--${username}`);
    const scopes = [
        "streaming",
        "user-read-email",
        "user-read-private",
        "playlist-read-private",
        "playlist-read-collaborative",
        "user-modify-playback-state",
        "user-read-playback-state",
        "user-read-currently-playing"
    ];

    const loginUrl = `https://accounts.spotify.com/authorize?client_id=${client_id}&response_type=code&redirect_uri=${encodeURIComponent(
        callBackUrl
    )}&scope=${encodeURIComponent(scopes.join(" "))}&state=${state}`;

    useEffect(() => {
        function handleClickOutside(event) {
            if (spotifyWidgetRef.current && !spotifyWidgetRef.current.contains(event.target)) {
                setShowSpotifyControls(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showSpotifyControls]);

    // Load Spotify Web Playback SDK
    useEffect(() => {
        if (!accessToken) return;

        // If SDK is already loaded
        if (window.Spotify) {
            initializePlayer();
        } else {
            // Load SDK and set callback
            const script = document.createElement("script");
            script.src = "https://sdk.scdn.co/spotify-player.js";
            script.async = true;
            document.body.appendChild(script);
            window.onSpotifyWebPlaybackSDKReady = initializePlayer;
        }

        const handleBeforeUnload = () => {
            if (playerRef.current) {
                playerRef.current.disconnect();
            }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [accessToken]);

    function initializePlayer() {
        if (!playerRef.current && accessTokenRef.current) {
            playerRef.current = new window.Spotify.Player({
                name: "DMBuddy",
                getOAuthToken: cb => {
                    cb(accessTokenRef.current);
                },   
                volume: 0.5
            });

            playerRef.current.addListener('ready', ({ device_id }) => {
                setDeviceId(device_id);
            });

            playerRef.current.addListener('player_state_changed', state => {
                setIsPaused(state.paused);
                setCurrentTrack(state.track_window.current_track);
                setIsShuffling(state.shuffle);
                setRepeatMode(state.repeat_mode === 0 ? "off" : state.repeat_mode === 1 ? "context" : "track");
            });

            playerRef.current.connect();
            setPlayer(playerRef.current);
        }
    }

    // On mount: if spotifyInfo is available, auto-load playlists and song
    useEffect(() => {
        if (spotifyInfo?.access_token && playlists === null) {
            getPlayListsAndSongs(spotifyInfo);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle Spotify Auth Success
    useEffect(() => {
        socket.on('spotifyAuthSuccess', (spotifyRes) => {
            console.log("Spotify Auth Success");
            getPlayListsAndSongs(spotifyRes)
        });

        socket.on('spotifyRefreshed', (spotifyRes) => {
            console.log("Spotify token refreshing");
            refreshSpotifyInfo(spotifyRes)
        });

    }, [socket]);

    // Handle encounter change to play saved song
    useEffect(() => {
        let songInfo = currentEncounter?.spotifyEncounterSongInfo
        // If there's a saved song from the encounter, play it
        let sameSong = currentTrack?.id === songInfo?.currentTrackId && selectedPlaylist === songInfo?.playlistId
        if (songInfo?.currentTrackId && songInfo?.playlistId && playlists?.length > 0) {
            if(!sameSong) {
                const { playlistId, currentTrackId } = songInfo;
                setSelectedPlaylist(playlistId);
                if(autoPlay) {
                    // auto play the next song
                    playPlaylist(playlistId, currentTrackId);
                } else {
                    // keep the current song playing and show next button
                    setShowNextSong(true)
                }
               
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentEncounter.encounterGuid]);

    const refreshSpotifyInfo = (spotifyRes) => {
        // accessTokenRef is used to keep OAuth Token in sync
        accessTokenRef.current = spotifyRes.access_token;
        setAccessToken(spotifyRes.access_token);
        setHeaders({ Authorization: `Bearer ${spotifyRes.access_token}` });

        if(spotifyRes.expires_in) {
            console.log("Set new refresh timer")
            // Save expiry time (expires_in is seconds) * 1000 to convert to ms
            let expires_in_ms = spotifyRes.expires_in * 1000
            // when we refresh start a new timer to refresh
            setTimeout(() => {
                console.log("Token expiring, send refresh request")
                socket.emit("spotifyRefreshToken", username)
            }, expires_in_ms - 5000); // reload 5 seconds before expiry    
        }
        
    }

    const getPlayListsAndSongs = async (spotifyRes) => {
        console.log("Authed! Fetching tracks and playlist");
        refreshSpotifyInfo(spotifyRes)

        // Get user's playlists
        try {
            await axios.get("https://api.spotify.com/v1/me/playlists", {
                headers: { Authorization: `Bearer ${spotifyRes.access_token}` },
                params: { limit: 50 }
            }).then(async res => {
                let playLists = res.data.items
                setPlaylists(playLists);
                // If there are playlists, set the first one as selected and fetch its tracks
                if (playLists?.length > 0) {
                    if(currentEncounter?.spotifyEncounterSongInfo?.playlistId) {
                        setSelectedPlaylist(currentEncounter?.spotifyEncounterSongInfo?.playlistId);
                        // set the currentsong to the saved song but stay paused 
                        const res = await fetch(`https://api.spotify.com/v1/tracks/${currentEncounter?.spotifyEncounterSongInfo?.currentTrackId}`, {
                            headers: { Authorization: `Bearer ${spotifyRes.access_token}` }
                        });
                        const data = await res.json();
                        setCurrentTrack({...data, firstLoad: true})

                    } else {
                        setSelectedPlaylist(playLists[0].id);
                    }
                }
            });
        } catch (err) {
            console.error("Failed to fetch playlists or songs:", err);
            setPlaylists([]);
            setPlaylistTracks([]);
        }
    };

    useEffect(() => {
        const fetchTracks = async () => {
            if (!accessToken || !selectedPlaylist) {
                setPlaylistTracks([]);
            } else if (playlistTracksCache.current[selectedPlaylist]) {
                // Check cache first if tracks for the selected playlist are already fetched
                setPlaylistTracks(playlistTracksCache.current[selectedPlaylist]);
            } else {
                try {
                    await axios.get(`https://api.spotify.com/v1/playlists/${selectedPlaylist}/tracks`,
                        {headers, params: { limit: 100 }}
                    ).then(res => {
                        // Save to cache
                        playlistTracksCache.current[selectedPlaylist] = res.data.items;
                        setPlaylistTracks(res.data.items);
                    });
                    
                } catch (err) {
                    console.error("Failed to fetch playlist tracks in fetchTracks:", err);
                    setPlaylistTracks([]);
                }
            }
        };

        fetchTracks();
    }, [selectedPlaylist]);

    // Play a playlist
    const playPlaylist = async (playlistId, trackId = null) => {
        if (!accessToken || !deviceId) return;

        // Wait until playlistTracks for this playlist are loaded
        let tracks = playlistTracksCache.current[playlistId];
        if (!tracks) {
            // Fetch tracks if not cached
            try {
                const res = await axios.get(
                    `https://api.spotify.com/v1/playlists/${playlistId}/tracks`,
                    { headers, params: { limit: 100 } }
                );
                tracks = res.data.items;
                playlistTracksCache.current[playlistId] = tracks;
            } catch (err) {
                console.error("Failed to fetch playlist tracks in playPlaylist:", err);
                return;
            }
        }

        if(trackId) {
            const found = tracks.some(item => item.track && item.track.id === trackId);
            if (found) {
                let data = {
                    context_uri: `spotify:playlist:${playlistId}`,
                    offset: { uri: `spotify:track:${trackId}` }
                };

                try {
                    setMusicShake(true)
                    await axios.put(`https://api.spotify.com/v1/me/player/play?device_id=${deviceId}`, data, { headers });
                    await axios.put(`https://api.spotify.com/v1/me/player/repeat?device_id=${deviceId}&state=${repeatMode}`, data, { headers });
                    setTimeout(() => {
                        setMusicShake(false)
                    }, 1000)
                } catch (err) {
                    console.error("Failed to play playlist/track:", err.response ? err.response.data : err);
                }
            }
        }
    };

    // Playback controls
    const handlePlayPause = async () => {
        // This adds the encounters saved song to spotify if no song had been chosen yet
        // firstLoad is only added during the first load process
        if(currentTrack?.firstLoad) {
            handlePlayTrack(currentTrack.id);
            setCurrentTrack({...currentTrack, firstLoad: false})
        }
        
        if (player) {
            isPaused ? player.resume() : player.pause();
        }
    };

    const handleSaveCurrentSong = () => {
        if (!currentTrack || !selectedPlaylist) {
            return;
        }

        let savedSongInfo = { currentTrackId: currentTrack.id, playlistId: selectedPlaylist, currentTrackName: currentTrack.name}
        setCurrentEncounter({
            ...currentEncounter,
            spotifyEncounterSongInfo: {...savedSongInfo}
        });

        socket.emit("saveSpotifySong", savedSongInfo, currentEncounter.encounterGuid);
    };

    const handleRemoveSavedSong = () => {
        setCurrentEncounter({
            ...currentEncounter,
            spotifyEncounterSongInfo: {currentTrackId: null, playlistId: null, currentTrackName: null}
        });

        socket.emit("removeSpotifySong", currentEncounter.encounterGuid);
    };

    const handleNext = () => { if (player) player.nextTrack(); };
    const handlePrev = () => { if (player) player.previousTrack(); };

    const handleShuffle = async () => {
        if (!accessToken || !deviceId) return;
        await axios.put(
            `https://api.spotify.com/v1/me/player/shuffle?device_id=${deviceId}&state=${!isShuffling}`,
            {}, { headers }
        );
        setIsShuffling(!isShuffling);
    };

    const handleRepeat = async () => {
        if (!accessToken || !deviceId) return;
        const nextMode = repeatMode === "off" ? "context" : repeatMode === "context" ? "track" : "off";
        await axios.put(
            `https://api.spotify.com/v1/me/player/repeat?device_id=${deviceId}&state=${nextMode}`,
            {}, { headers }
        );
        setRepeatMode(nextMode);
    };

    // Playlist selection
    const handlePlaylistChange = (e) => {
        setSelectedPlaylist(e.target.value);
    };

    // Play a specific track in the playlist
    const handlePlayTrack = (trackId) => {
        playPlaylist(selectedPlaylist, trackId);
    };

    const isListSongCurrentSong = (playlistTrack) => {
        if(!currentTrack?.id) return

        // If no id match (because of bug) - check for name match
        let idMatch = currentTrack.id === playlistTrack.id
        return idMatch || currentTrack.name === playlistTrack.name
    }

    const isCurrentSongSaved = currentTrack?.id && currentEncounter?.spotifyEncounterSongInfo?.currentTrackId === currentTrack?.id

    const isListSongSavedSong = (playlistTrack) => {
        let savedInfo = currentEncounter?.spotifyEncounterSongInfo;
        if(!savedInfo?.currentTrackId) return

        // If no id match (because of bug) - check for name match
        let idMatch = savedInfo.currentTrackId === playlistTrack.id
        return idMatch || savedInfo.currentTrackName === playlistTrack.name
    }

    const handleAutoPlay = (e) => {
        setAutoPlay(e.target.checked)
        socket.emit("setUpdatePref", "spotifyAutoPlay", e.target.checked, username)
    }
    
    const handleNextQueuedSong = (e) => {
        // set to 1 to still show and show animation before leaving
        setShowNextSong(1)
        setTimeout(() => {
            setShowNextSong(false)
        }, 500)
    }

    return (
        <div ref={spotifyWidgetRef}>
            <OptionButton
                src={musicIcon}
                message={<>Spotify Controls {currentTrack?.name && <>- Current: {currentTrack?.name}</>}</>}
                onClickFunction={() => setShowSpotifyControls(v => (!v))}
                wrapperClassName={musicShake && 'hiddenShake'}

            />
            {showNextSong && accessToken && 
                <OptionButton
                    src={playNext}
                    message={`Play: ${currentEncounter?.spotifyEncounterSongInfo?.currentTrackName}`}
                    onClickFunction={() => {
                        playPlaylist(currentEncounter?.spotifyEncounterSongInfo?.playlistId, currentEncounter?.spotifyEncounterSongInfo?.currentTrackId)
                        handleNextQueuedSong()
                    }}
                    wrapperClassName={showNextSong === true ? 'autoPlayButtonWrapper' : 'autoPlayOut'}
                    imgClassName="autoPlayButtonImg"
                />
            }
            
            <div style={{ top: '-35px', right: showSpotifyControls ? '385px' : '9999px', zIndex: 1, position: "absolute"}}>
                <div
                    style={{
                        borderRadius: 16,
                        overflow: "hidden",
                        boxShadow: "0 4px 16px #0003",
                        padding: "10px 24px 4px 24px",
                        background: "#181818",
                        color: "#fff",
                        width: 400,
                        maxWidth: 600,
                        marginLeft: "auto",
                        marginRight: "auto",
                        position: "relative"
                    }}
                >
                    <button
                        onClick={() => setShowSpotifyControls(prev => !prev)}
                        style={{
                            position: "absolute",
                            top: 5,
                            right: 12,
                            background: "transparent",
                            border: "none",
                            color: "#fff",
                            fontSize: 26,
                            cursor: "pointer",
                            zIndex: 2,
                            padding: 0,
                            lineHeight: 1
                        }}
                        aria-label="Close Spotify Widget"
                        title="Close"
                    >
                        ×
                    </button>
                    <div style={{ borderBottom: '1px solid rgb(71, 71, 71)', padding: "0 0 5px 0", margin: '0 0 5px 0'}}>
                        <h2 style={{paddingBottom: "0", margin: '0', fontWeight: 700, fontSize: 22, letterSpacing: 1 }}>
                            Spotify Player
                        </h2>
                        {accessToken && 
                            <span style={{ display: 'flex', justifyContent: 'space-between', color: "#bbb", fontSize: 15 }}>
                                Encounter: {currentEncounter.encounterName} 
                                <span style={{display: 'flex', alignItems: 'center'}}>
                                    Auto Play <input type="checkbox" style={{margin: '0 0 0 5px', accentColor: "#rgb(72 159 72)"}} checked={autoPlay} onChange={(e) => handleAutoPlay(e)}/>
                                </span>
                            </span>
                        }
                        
                    </div>
                    
                    
                    {!accessToken && (
                        <div style={{ margin: "24px 0", textAlign: "center" }}>
                            {username === 'Username' &&
                                <div style={{ color: "#FFF", fontSize: 13, margin: 10 }}>
                                    Sign in to DmBuddy account to use Spotify 
                                </div>
                            }
                            <button
                                onClick={() => window.open(loginUrl, "_blank", "width=500,height=700")}
                                style={{
                                    display: "inline-block",
                                    background: username === 'Username' ? "#777777" : "#1DB954",
                                    color: username === 'Username' ? "#AAAAAA" : "#fff",
                                    border: "none",
                                    borderRadius: 24,
                                    padding: "12px 32px",
                                    fontWeight: "bold",
                                    fontSize: 16,
                                    textDecoration: "none",
                                    boxShadow: "0 2px 8px #0002",
                                    transition: "background 0.2s",
                                    cursor: "pointer"
                                }}
                                disabled={username === 'Username'}
                            >
                                Sign in to Spotify
                            </button>
                            <div style={{ color: "#bbb", fontSize: 13, marginTop: 10 }}>
                                Ask DmBuddy Nic on Discord - Spotify Premium Required
                            </div>
                        </div>
                    )}

                    {accessToken && (
                        <>
                            <div className="spWidgetTopContainer">
                                <div>
                                    <img src={currentTrack?.album?.images[0]?.url || musicIcon} alt="album" width={95} height={95} style={{ borderRadius: 10, boxShadow: "0 2px 8px #0005" }} />
                                </div>
                                <div className="spWidgetControls">
                                    <div style={{ marginBottom: 8, textAlign: "center" }}>
                                        <button onClick={handleShuffle} style={{ ...btnStyle, background: isShuffling ? "#1DB954" : "#2220" }}>
                                            🔀
                                        </button>
                                        <button onClick={handlePrev} style={btnStyle}>⏮</button>
                                        <button onClick={handlePlayPause} style={btnStyle}>
                                            {isPaused ? "▶️" : "⏸"}
                                        </button>
                                        <button onClick={handleNext} style={btnStyle}>⏭</button>
                                        
                                        <button onClick={handleRepeat} style={{ ...btnStyle, background: repeatMode !== "off" ? "#1DB954" : "#2220" }}>
                                            <span style={{ position: "relative"}}>
                                                🔁
                                                {repeatMode === "track" && (
                                                    <span style={{
                                                            position: "absolute",
                                                            top: 12,
                                                            right: -3,
                                                            color: "#fff",
                                                            background: "#117131",
                                                            borderRadius: "50%",
                                                            width: 14,
                                                            height: 14,
                                                            fontSize: 10,
                                                            fontWeight: "bold",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            pointerEvents: "none",
                                                            outline: '1px solid white'
                                                        }}
                                                    >
                                                        1
                                                    </span>
                                                )}
                                            </span>
                                        </button>
                                    </div>
                                    <div style={{display: 'flex'}}>
                                        <button
                                            disabled={!currentTrack || !selectedPlaylist}
                                            onClick={handleSaveCurrentSong}
                                            style={{
                                                background: !currentTrack || !selectedPlaylist ? "#888" : "rgb(0 117 41)",
                                                color: "#fff",
                                                borderRadius: 20,
                                                padding: "8px 18px",
                                                cursor: !currentTrack || !selectedPlaylist ? "not-allowed" : "pointer",
                                                fontSize: 14,
                                            }}
                                        >
                                            {isCurrentSongSaved 
                                                ? `Saved to: ${currentEncounter.encounterName}`
                                                : (currentTrack?.id ? 'Save Song to Encounter' : `Play a Song to Save`)
                                            }
                                        </button>
                                        {currentEncounter?.spotifyEncounterSongInfo?.currentTrackId && 
                                            <button style={{all: "unset", cursor: "pointer", marginLeft: "5px" }} onClick={handleRemoveSavedSong} title="Remove Saved Song">
                                                <svg
                                                    width={20}
                                                    height={20}
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                >
                                                    <circle cx="12" cy="12" r="12" fill="#E53935" />
                                                    <line
                                                        x1="8"
                                                        y1="8"
                                                        x2="16"
                                                        y2="16"
                                                        stroke="#fff"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />
                                                    <line
                                                        x1="16"
                                                        y1="8"
                                                        x2="8"
                                                        y2="16"
                                                        stroke="#fff"
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />
                                                </svg>
                                            </button>
                                        }
                                    </div>
                                </div>
                                <SpotifyVolumeSlider player={player} />
                            </div>
                            <div style={{ fontWeight: 600, fontSize: 17, margin: '5px 0 0 0' }}>
                                {currentTrack?.name} <span style={{ color: "#bbb", fontSize: 15 }}> - {currentTrack?.artists.map(a => a.name).join(", ")  || "No Song Currently Playing"}</span>
                            </div>
                            <SpotifyScrubber player={player} currentTrack={currentTrack} isPaused={isPaused} />
                            {!deviceId ? (
                                <div style={{ textAlign: "center", margin: "32px 0" }}>
                                    <div className="spotify-spinner" style={{
                                        display: "inline-block",
                                        width: 40,
                                        height: 40,
                                        border: "4px solid #444",
                                        borderTop: "4px solid #1DB954",
                                        borderRadius: "50%",
                                        animation: "spin 1s linear infinite"
                                    }} />
                                    <div style={{ color: "#bbb", marginTop: 10 }}>Loading Spotify Player...</div>
                                    <style>
                                        {`@keyframes spin { 100% { transform: rotate(360deg); } }`}
                                    </style>
                                </div>
                            ) : (
                                <>
                                {playlists?.length > 0 && (
                                    <div style={{ margin: '4px 0 4px 0', paddingTop: "4px", borderTop: '1px solid rgb(71, 71, 71)'}}>
                                        <select
                                            value={selectedPlaylist || ""}
                                            onChange={handlePlaylistChange}
                                            style={{
                                                padding: "8px 16px",
                                                borderRadius: 8,
                                                border: "1px solid #333",
                                                background: "#222",
                                                color: "#fff",
                                                fontSize: 15
                                            }}
                                        >
                                            {playlists.map(pl => (
                                                <option key={pl.id} value={pl.id} 
                                                    style={{background: currentEncounter?.spotifyEncounterSongInfo?.playlistId === pl.id ? "grey" : ""}}
                                                >
                                                    {pl.name} {currentEncounter?.spotifyEncounterSongInfo?.playlistId === pl.id && ' || Saved Song'}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {playlistTracks?.length > 0 && (
                                    <div style={{
                                        maxHeight: 250,
                                        overflowY: "auto",
                                        background: "#222",
                                        borderRadius: 10,
                                        padding: 8,
                                        marginBottom: 16
                                    }}>
                                        {playlistTracks.map((item, index) => {
                                            const track = item.track;
                                            if (!track) return null;

                                            return (
                                                <div
                                                    key={track.id + index}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        margin: "4px 0",
                                                        padding: "0px 0",
                                                        borderBottom: "1px solid #333",
                                                        cursor: "pointer",
                                                        borderRadius: 8,
                                                    }}
                                                    onClick={() => handlePlayTrack(track.id)}
                                                    onMouseEnter={e => e.currentTarget.style.boxShadow = "0 0 0 2px #888"}
                                                    onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                                                >
                                                    <img
                                                        src={track.album.images[2]?.url || track.album.images[0]?.url}
                                                        alt="cover"
                                                        width={40}
                                                        height={40}
                                                        style={{ borderRadius: 6, marginRight: 10 }}
                                                    />
                                                    <div style={{ flex: 1}}>
                                                        <div style={{ display: 'flex', fontWeight: 500, fontSize: 15}}>{track.name}</div>
                                                        <div style={{ display: 'flex', color: "#bbb", fontSize: 13 }}>{track.artists.map(a => a.name).join(", ")} </div>
                                                    </div>
                                                    {isListSongCurrentSong(track) && <CurrentlyPlayingAnimation isPaused={isPaused}/>} 
                                                    {isListSongSavedSong(track) &&
                                                        <svg
                                                            width={15}
                                                            height={15}
                                                            viewBox="0 0 24 24"
                                                            fill="none"
                                                            style={{ display: "inline-block", verticalAlign: "middle", marginRight: "10px" }}
                                                        >
                                                            <circle cx="12" cy="12" r="12" fill="#1DB954" />
                                                            <path
                                                                d="M7 12.5l3 3 7-7"
                                                                stroke="#fff"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                fill="none"
                                                            />
                                                        </svg>
                                                    }

                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>)}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const btnStyle = {
    background: 'none',
    color: "#fff",
    border: "none",
    borderRadius: 20,
    padding: "8px 10px",
    margin: "0 4px",
    fontSize: 18,
    cursor: "pointer",
    transition: "background 0.2s"
};

export default SpotifyWidget;